import { router } from '@/router'
import { useAnalyticsSessionStore } from '@/stores/analyticsSession'
import { getEffectiveCadenceInWeeks } from '@/utils/plan'
import {
  accountPlanType,
  pickPrimarySubscription,
  readAddress,
  readDogs,
  readIsAuthenticated,
  readProfile,
  readSubscriptions,
} from '@/analytics/properties'
import { SubscriptionStatus } from '@lyka-pet-food/contracts'
import type { SuperProperties, PeopleProfileProperties } from '@/types/analytics'

const REACTIVATION_ELIGIBLE_STATUSES: string[] = [SubscriptionStatus.paused, SubscriptionStatus.cancelled]

const locationFromRoute = (): SuperProperties['location'] =>
  router.currentRoute.value.meta.analyticsLocation ?? null

/**
 * Super properties attached to every analytics event (Segment + CIO + Mixpanel).
 * Computed at event time from the vue-query cache, auth store, and router meta so they
 * are always current without watchers or setup-context coupling. Device/platform context
 * is omitted — Segment captures that automatically in event context. `location` is a
 * Lyka-specific logical surface tag (deliveries / dogs / raf / account) that Segment
 * cannot derive from the URL, so it is kept here.
 */
export const getSuperProperties = (): SuperProperties => {
  const profile = readProfile()
  const subscription = pickPrimarySubscription(readSubscriptions())
  const activeDogs = readDogs().filter((d) => !d.deactivatedAt)
  const status = subscription?.status ?? null
  const session = useAnalyticsSessionStore()

  return {
    brand: 'lyka',
    source_system: 'phoenix',
    location: locationFromRoute(),
    is_authenticated: readIsAuthenticated(),
    tenant: profile?.tenantType ?? null,
    subscription_status: status,
    is_reactivation_eligible: status != null && REACTIVATION_ELIGIBLE_STATUSES.includes(status),
    account_plan_type: accountPlanType(subscription),
    delivery_frequency: subscription ? getEffectiveCadenceInWeeks(subscription) : null,
    dog_count: activeDogs.length,
    subscription_id: subscription?.id ?? null,
    session_id: session.sessionId,
  }
}

/** People profile properties (Mixpanel People API) — PII + account context. */
export const getPeopleProfileProperties = (): PeopleProfileProperties => {
  const profile = readProfile()
  const subscription = pickPrimarySubscription(readSubscriptions())
  const activeDogs = readDogs().filter((d) => !d.deactivatedAt)
  const address = readAddress()

  return {
    email: profile?.email ?? null,
    first_name: profile?.firstName ?? null,
    last_name: profile?.lastName ?? null,
    phone: profile?.phoneNumber ?? null,
    tenant: profile?.tenantType ?? null,
    cognito_user_id: profile?.cognitoUserId ?? null,
    brand: 'lyka',
    subscription_status: subscription?.status ?? null,
    account_plan_type: accountPlanType(subscription),
    delivery_frequency: subscription ? getEffectiveCadenceInWeeks(subscription) : null,
    dog_count: activeDogs.length,
    state: address?.state ?? null,
  }
}
